"use client";

import React from "react";
import { Box, Container, Typography, Button, Stack, Chip } from "@mui/material";
import Image from "next/image";
import { useTranslation } from "react-i18next";
import NextLink from "next/link";

/**
 * Reusable Hero Section
 *
 * Ye component ab generic hai — koi bhi page/feature isko apni
 * images, translation namespace, aur CTA link ke saath use kar sakta hai.
 *
 * Props:
 * @param {string} translationNamespace - i18n keys ka prefix, e.g. "featureOneHero"
 *        Expects these keys inside that namespace: chip, title1, titleHighlight1,
 *        desc1, cta, trustedBy, rating, imageAlt
 * @param {string} imageUrl        - LTR (default) image URL
 * @param {string} imageUrlAr      - RTL/Arabic image URL (falls back to imageUrl if not given)
 * @param {string} ctaHref         - link jahan CTA button navigate kare
 * @param {number} imageWidth      - next/image intrinsic width
 * @param {number} imageHeight     - next/image intrinsic height
 * @param {object} imageBoxSx      - optional sx overrides for the right-side image container
 *        (agar future me kisi page ko different breakpoints/positioning chahiye)
 */
const FeatureHeroSection = ({
  translationNamespace = "featureOneHero",
  imageUrl,
  imageUrlAr,
  ctaHref = "/free-trial-of-ai-recruiting-tool",
  imageWidth = 1100,
  imageHeight = 750,
  imageBoxSx = {},
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  const ns = translationNamespace;
  const resolvedImageUrl = isRTL ? (imageUrlAr || imageUrl) : imageUrl;

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: "#FFFFFF",
        direction: isRTL ? "rtl" : "ltr",
        overflow: "hidden",
        mt: "3%",
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            gap: { xs: 4, sm: 5, md: 3 },
            px: { xs: 2, sm: 3, md: 0 },
            mt: {
              xs: isRTL ? "20%" : "24%",
              sm: isRTL ? "12%" : "10%",
              md: isRTL ? "3%" : "5%",
              lg: isRTL ? "1%" : "2%",
              xl: isRTL ? "1%" : "1%",
            },
            pb: { xs: 6, sm: 8, md: 6 },
          }}
        >
          {/* Left Content */}
          <Box
            sx={{
              flex: 1,
              maxWidth: { xs: "100%", md: "48%" },
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              textAlign: isRTL ? "right" : "left",
            }}
          >
            <Chip
              label={t(`${ns}.chip`)}
              sx={{
                backgroundColor: "#F2E9FE",
                border: "1px solid #6D50E01A",
                mb: { xs: 3, sm: 3 },
                color: "#6B24CE",
                fontSize: { xs: "12px", sm: "13px", md: "13px" },
                fontWeight: "500",
                px: { xs: 1, sm: 1.2 },
                py: { xs: 1.6, sm: 2 },
                fontFamily: "var(--font-outfit)",
                borderRadius: "40px",
                textTransform: "none",
              }}
            />

            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontSize: {
                  xs: "2rem",
                  sm: "2.5rem",
                  lg: "2.3rem",
                  xl: "3rem",
                },
                fontWeight: "700",
                mb: { xs: 2, sm: 2.5, md: 2.5 },
                lineHeight: { xs: 1.25, md: 1.2 },
                fontFamily: "var(--font-plusjakarta)",
                color: "#14101C",
                textAlign: isRTL ? "right" : "left",
                width: { xs: "100%", md: "90%" },
              }}
            >
              {t(`${ns}.title1`)}
              <span style={{ color: "#9048FB" }}>
                {t(`${ns}.titleHighlight1`)}
              </span>
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: "#544E68",
                mb: { xs: 4, sm: 4, md: 3 },
                fontSize: {
                  xs: "0.875rem",
                  sm: "1rem",
                  lg: "17px",
                  xl: "18px",
                },
                maxWidth: { xs: "100%", md: "100%" },
                fontWeight: "400",
                fontFamily: "var(--font-outfit)",
                lineHeight: { xs: 1.5, md: 1.6 },
                textAlign: isRTL ? "right" : "left",
                width: "100%",
              }}
            >
              {t(`${ns}.desc1`)}
            </Typography>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 1.5, sm: 2 }}
              alignItems={{ xs: "stretch", sm: "flex-start" }}
              sx={{ mb: { xs: 4, sm: 5, md: 5 }, width: "100%" }}
            >
              <NextLink href={ctaHref} passHref legacyBehavior>
                <Button
                  component="a"
                  rel="noopener noreferrer"
                  sx={{
                    bgcolor: "#9048FB",
                    color: "#FFFFFF",
                    textTransform: "none",
                    px: { xs: 2.5, sm: 3, md: 3 },
                    py: { xs: 1.2, sm: 1.2, md: 1.4 },
                    height: { xs: "48px", sm: "52px", md: "50px" },
                    fontFamily: "var(--font-outfit)",
                    fontSize: { xs: "0.85rem", sm: "0.95rem", lg: "1rem" },
                    fontWeight: "500",
                    borderRadius: "10px",
                    boxShadow: "none",
                    transition: "all 0.3s ease-in-out",
                    "&:hover": {
                      bgcolor: "#9048FB",
                      transform: "scale(1.05)",
                    },
                  }}
                >
                  {t(`${ns}.cta`)}
                </Button>
              </NextLink>
            </Stack>

            {/* Trust row */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 1, sm: 2 }}
              divider={
                <Box
                  sx={{
                    display: { xs: "none", sm: "block" },
                    width: "1px",
                    height: "16px",
                    bgcolor: "#0206171F",
                  }}
                />
              }
              alignItems={{ xs: "flex-start", sm: "center" }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "12px", sm: "13px" },
                  fontWeight: "600",
                  color: "#14101C",
                  fontFamily: "var(--font-plusjakarta)",
                }}
              >
                {t(`${ns}.trustedBy`)}
              </Typography>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography
                  sx={{
                    fontSize: { xs: "12px", sm: "13px" },
                    fontWeight: "500",
                    color: "#544E68",
                    fontFamily: "var(--font-outfit)",
                    ml: 0.5,
                  }}
                >
                  {t(`${ns}.rating`)}
                </Typography>
              </Stack>
            </Stack>
          </Box>

          {/* Right Content - Image */}
          <Box
            sx={{
              flex: 1,
              display: { xs: "none", md: "flex" },
              justifyContent: "center",
              alignItems: "flex-start",
              position: "relative",
              overflow: "hidden",
              mr: isRTL ? 0 : { lg: "-16%", xl: "-67%" },
              ml: isRTL ? { lg: "-16%", xl: "-66%" } : 0,
              "@media (min-width: 900px) and (max-width: 1200px)": {
                mr: isRTL ? 0 : "-10%",
                ml: isRTL ? "-10%" : 0,
                objectFit: "contain",
                height: "500px",
              },
              "@media (min-width: 1000px) and (max-width: 1200px)": {
                mr: isRTL ? 0 : "-3%",
                ml: isRTL ? "-30%" : 0,
              },
              "@media (min-width:1100px) and (max-width:1300px)": {
                mr: isRTL ? 0 : "-10%",
                ml: isRTL ? "-30%" : 0,
                mt: isRTL ? "6%" : 0,
                height: "500px",
              },
              "@media (min-width:1400px) and (max-width:1535px)": {
                maxWidth: "95%",
                ml: isRTL ? "-40%" : 0,
                mr: isRTL ? 0 : "-30%",
                height: isRTL ? "700px" : "580px",
              },
              "@media (min-width:2000px) and (max-width:2100px)": {
                mr: isRTL ? 0 : "-38%",
                ml: isRTL ? "-75%" : 0,
                height: isRTL ? "700px" : "600px",
              },
              "@media (min-width:2100px) and (max-width:2350px)": {
                mr: isRTL ? 0 : "-50%",
                ml: isRTL ? "-110%" : 0,
                height: isRTL ? "700px" : "600px",
              },
              "@media (min-width:2400px) and (max-width:2600px)": {
                mr: isRTL ? 0 : "-56%",
                ml: isRTL ? "-120%" : 0,
                height: isRTL ? "700px" : "600px",
              },
              "@media (min-width:1536px) and (max-width:1999px)": {
                mr: isRTL ? 0 : "-33%",
                ml: isRTL ? "-50%" : 0,
                height: isRTL ? "650px" : "680px",
              },
              "@media (min-width:1750px) and (max-width:1999px)": {
                mr: isRTL ? 0 : "-50%",
                ml: isRTL ? "-50%" : 0,
                height: isRTL ? "700px" : "760px",
              },
              width: { md: "auto" },
              height: "600px",
              ...imageBoxSx,
            }}
          >
            <Image
              src={resolvedImageUrl}
              alt={t(`${ns}.imageAlt`)}
              width={imageWidth}
              height={imageHeight}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
              priority
              fetchPriority="high"
              quality={90}
            />
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default FeatureHeroSection;
